import React from 'react';
import { Button } from '@/app/components/ui/button';
import { Cuboid, ArrowLeft } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';

import { LanguageSwitcher } from '@/app/components/LanguageSwitcher';

interface LegalLayoutProps {
  children: React.ReactNode;
  title: string;
  onBack: () => void;
}

export function LegalLayout({ children, title, onBack }: LegalLayoutProps) {
  const { t } = useLanguage();

  return (
    <div className="h-screen w-full overflow-y-auto overflow-x-hidden bg-slate-50 scroll-smooth">
      <div className="min-h-screen flex flex-col text-slate-900 font-sans selection:bg-purple-100 selection:text-purple-900">
        
        {/* --- Navigation --- */}
        <header className="sticky top-0 z-50 w-full bg-slate-50/80 backdrop-blur-md border-b border-slate-200/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div 
                className="flex items-center gap-2 font-bold text-xl tracking-tight cursor-pointer"
                onClick={onBack}
              >
                <div className="bg-purple-600 text-white p-1.5 rounded-lg shadow-sm">
                  <Cuboid className="w-5 h-5" />
                </div>
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-700 to-indigo-600">
                  Isoblock
                </span>
              </div>
              
              <div className="flex items-center gap-4">
                <LanguageSwitcher />
                <Button variant="ghost" onClick={onBack} className="text-slate-600 hover:text-purple-700 hover:bg-purple-50 flex items-center gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('common.toLandingPage')}</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 py-12 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-8">{title}</h1>
          <div className="prose prose-slate max-w-none bg-white p-8 rounded-2xl shadow-sm border border-slate-200">
            {children}
          </div>
        </main>

        {/* --- Footer --- */}
        <footer className="bg-white py-12 border-t border-slate-200 mt-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2 font-bold text-lg text-slate-900">
              <Cuboid className="w-5 h-5 text-purple-600" />
              Isoblock
            </div>
            <p className="text-slate-500 text-sm">
              &copy; {new Date().getFullYear()} Isoblock. Alle Rechte vorbehalten.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
